/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import type {
  default as Value,
  DynamicValue,
} from '@linc-technologies/linced-common/models/dynamic-field/value';
import type StoreService from '@linc-technologies/linced-common/services/store';
import {
  addFieldTo,
  cancelPendingDrafts,
  buildFieldMap,
  fieldOf,
  fieldProps,
  setFieldValueOn,
  type DynamicFieldMap,
  type DynamicFieldProps,
} from '@linc-technologies/linced-common/utils/dynamic-fields';

// DynamicFieldsModel is the base class of a model that holds dynamic fields
// (the proto `map<string, FieldValue> fields`). DynamicFieldsFragment is the
// same for a fragment.
// This file is not re-exported from app/models, so the store cannot use it as
// a model.
export default class DynamicFieldsModel extends Model {
  @fragmentArray('dynamic-field/value')
  declare fieldsArray: FragmentArray<Value>;

  // fields gives a map of the dynamic field values by field key.
  @computed('fieldsArray.@each.key')
  get fields(): DynamicFieldMap {
    return buildFieldMap(this.fieldsArray);
  }

  // _addField adds a new field to the record's fieldsArray.
  // A new field replaces a field with the same key on the record.
  _addField(field: DynamicFieldProps): void {
    addFieldTo(this, field);
  }

  // addField adds a new field with the zero value of its datatype.
  addField(key: string, datatype: number, requiredRole?: string): void {
    this._addField(fieldProps(key, datatype, requiredRole));
  }

  // field gives the value fragment of a field, or undefined.
  field(key: string): Value | undefined {
    return fieldOf(this, key);
  }

  // fieldValue gives the value of a field, or undefined.
  fieldValue<T extends DynamicValue = DynamicValue>(
    key: string,
  ): T | undefined {
    return fieldOf(this, key)?.value as T | undefined;
  }

  // setFieldValue sets the value of a field. If the record has no such field,
  // it adds the field with the datatype first.
  setFieldValue(key: string, value: unknown, datatype?: number): void {
    setFieldValueOn(this, key, value, datatype);
  }

  // rollbackAttributes also stops the save of the form defaults (drafts).
  // It cancels them first, because the rollback of a new record unloads it.
  rollbackAttributes(): void {
    cancelPendingDrafts(this.store as StoreService, this);
    super.rollbackAttributes();
  }
}
